import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import {
  Document,
  DocumentCategory,
  DocumentIdentifier,
  DocumentPropertyIdentifier,
  DocumentType,
  PropertiesService,
  RealProperty,
  TrackingService,
  VaultService
} from '@eemo/api-client';
import { InputTrimDirective } from '../../extensions/inputTrimDirective';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';
import { LinkedObject } from '../file-upload-dialog/file-upload-dialog.component';

export class DocUpdateDialogData {
  doc: Document;
  prop: RealProperty;
}

@Component({
  selector: 'app-doc-update-dialog',
  templateUrl: './doc-update-dialog.component.html',
  styleUrls: ['./doc-update-dialog.component.scss'],
  standalone: true,
  imports: [
    ModalBaseComponent,
    NgTemplateOutlet,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    InputTrimDirective,
    NgIf,
    MatRadioModule,
    MatSelectModule,
    NgFor,
    MatOptionModule
  ]
})
export class DocUpdateDialogComponent implements OnInit {
  docForm: UntypedFormGroup;
  dialogTitle: string;
  isLoading = false;

  mode: 'categorize' | 'identify' = 'categorize';

  categories: DocumentCategory[];
  categoriesName: { [key: string]: string } = {};
  types: DocumentType[];
  typesName: { [key: string]: string } = {};
  identifiers: DocumentIdentifier[];
  identifiersName: { [key: string]: string } = {};

  showLinkedObject = false;
  linkObjectId: string;
  linkedObjects: LinkedObject[];

  canIdentifyDocument = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DocUpdateDialogData,
    private fb: UntypedFormBuilder,
    private vaultService: VaultService,
    private alertService: AlertService,
    public dialogRef: MatDialogRef<DocUpdateDialogComponent>,
    private propHelper: PropertyHelper,
    private userHelper: UserHelper,
    private propService: PropertiesService,
    private trackingService: TrackingService
  ) {
    this.dialogTitle = 'Modifier le document';
    this.categories = this.propHelper.getDocumentCategories();
    this.categories.forEach(
      (c) => (this.categoriesName[c] = this.propHelper.getDocumentCategoryName(c, this.data.prop.type))
    );
    this.types = Object.values(DocumentType);
    this.types.forEach((t) => (this.typesName[t] = this.propHelper.getDocumentTypeName(t)));
    this.identifiers = Object.values(DocumentPropertyIdentifier);
    this.identifiers.forEach((i) => (this.identifiersName[i] = this.propHelper.getDocumentIdentifierName(i)));
    this.canIdentifyDocument = this.userHelper.canIdentifyDocument(this.data.prop);
  }

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.docForm.get(field);
  }

  ngOnInit() {
    this.docForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      description: ['', Validators.maxLength(160)],
      identifier: [DocumentIdentifier.NONE, Validators.required],
      category: ['', Validators.required],
      type: ['', Validators.required],
      linkObjectId: ['']
    });

    this.f('category').valueChanges.subscribe(() => {
      this.onCategoryChange();
    });

    if (this.data.doc) {
      this.docForm.patchValue(this.data.doc);
      if (this.data.doc.identifier !== DocumentIdentifier.NONE) {
        this.mode = 'identify';
      }
      this.linkObjectId = this.data.doc.linkObjectId;
    }
  }

  updateMode(event: MatRadioChange) {
    this.mode = event.value;
  }

  onSubmit() {
    if (this.docForm.valid) {
      this.isLoading = true;
      if (!this.f('linkObjectId').value) {
        this.f('linkObjectId').setValue(null);
      }
      if (this.mode === 'categorize') {
        this.f('identifier').setValue(DocumentIdentifier.NONE);
      }
      this.vaultService.updateDocument(this.data.doc.uid, this.docForm.value).subscribe({
        next: () => {
          this.dialogRef.close(true);
          this.alertService.success('Modification du document effectuée');
        },
        error: () => {
          this.dialogRef.close(false);
        }
      });
    }
  }
  onNoClick(): void {
    this.dialogRef.close(false);
  }

  onCategoryChange() {
    this.linkedObjects = [];
    const categ = this.f('category').value;
    this.showLinkedObject = false;
    if (categ === DocumentCategory.WORK) {
      this.propService.getRealPropertyWorkList(this.data.prop.uid).subscribe((data) => {
        this.showLinkedObject = data.length > 0;
        data.forEach((i) => this.linkedObjects.push({ uid: i.uid, name: i.summary }));
      });
    }
    if (categ === DocumentCategory.MAINTENANCE) {
      this.trackingService.getRealPropertyMaintenanceList(this.data.prop.uid).subscribe((data) => {
        this.showLinkedObject = data.length > 0;
        data.forEach((i) => this.linkedObjects.push({ uid: i.uid, name: i.summary }));
      });
    }
    if (categ === DocumentCategory.EQUIPMENT) {
      this.propService.getRealPropertyEquipmentList(this.data.prop.uid).subscribe((data) => {
        this.showLinkedObject = data.length > 0;
        data.forEach((i) => this.linkedObjects.push({ uid: i.uid, name: i.name }));
      });
    }
  }
}
